import React, { useState, useEffect, useMemo, useCallback } from "react";
import { MobileDesktopWrapper } from "src/mobile/mobile-wrapper/mobile-wrapper.component.js";
import { MfaType } from "./mfa-type.component";
import { MfaTimeout } from "./mfa-timeout.component";
import { SubmitMfa } from "./submit-mfa.component";
import { sendCode, getMfaTypes, createMfaMethod, sendSetupMfaCode } from "./code-mfa.resource";
import { MobileMfaVerified } from "./mobile-mfa-verified.component";
import { SetupMfaPhone } from "./setup-mfa-phone.component";
import QueryString from "query-string";
import CheckAuthCookieAndRefresh from "src/login/check-auth-cookie-and-refresh/check-auth-cookie-and-refresh.component.js";
import { throttle } from "lodash";
import { useWhiteLabelSettings } from "src/white-label-settings/white-label-settings.hook.js";
import { a, m, useCss, k } from "kremling";
import { handleError } from "src/error";
import { redirectToOnboarding } from "src/onboarding/onboarding-navigation.helper.js";
import { CpCase, CpLoader, CpSwitch } from "@canopytax/understory";
import { useMfaNavigation } from "./use-mfa-navigation";
import { tw } from "src/tailwind";

export default function CodeMfa(props) {
  const { location, mobile } = props;
  const [mfaTypes, setMfaTypes] = useState(
    window.history.state ? window.history.state.mfaTypes : location?.state?.mfaTypes
  );
  const [mfaType, setMfaType] = useState(mfaTypes?.[0] || {});
  const [mobileVerified, setMobileVerified] = useState(false);
  const [onSetupMfa, setOnSetupMfa] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);

  const { step, bannerText, navigate } = useMfaNavigation();
  const [isWhiteLabelDomain, whiteLabelSettings] = useWhiteLabelSettings();
  const scope = useCss(css);

  const uuid = useMemo(() => {
    // uuid will be passed as a query param if logging in to client portal via main canopy login.
    return (
      (window.history.state ? window.history.state.uuid : location?.state?.uuid) ||
      QueryString.parse(location.search).uuid
    );
  }, [location]);

  useEffect(() => {
    // If logging in to client portal from main canopy login, mfaTypes will be undefined and we must fetch them
    if (uuid && !mfaTypes) {
      getMfaTypes(uuid)
        .then((response) => {
          setMfaTypes(response.mfa_methods);
          setMfaType(response.mfa_methods?.[0]);
        })
        .catch(handleError);
    }
  }, [uuid, mfaTypes]);

  const redirectUrl = useMemo(() => {
    const parsed = QueryString.parse(location.search);
    return parsed.redirect_url;
  }, [location]);

  const invalidateSession = useCallback(() => {
    navigate("mfa-timeout", "Authentication Timeout");
  }, [navigate]);

  const routeToSignIn = () => {
    navigate("mfa-selection", "");
    props.history.push({ pathname: "/login", search: location.search });
  };

  const routeToMfaVerification = useCallback(() => navigate("mfa-verification", "Enter One-Time Passcode"), [navigate]);

  const sendVerificationCode = useCallback(
    (willLoad = true) => {
      if (mfaType.type === "totp") {
        routeToMfaVerification();
        return;
      }
      setLoading(willLoad);
      if (mfaType.type === "totp") {
        routeToMfaVerification();
        return;
      }
      sendCode(uuid, mfaType.id)
        .then(routeToMfaVerification)
        .catch((err) => {
          if (err.status === 401) {
            invalidateSession();
          } else {
            handleError(err);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [uuid, mfaType, invalidateSession, routeToMfaVerification]
  );

  const sendPhoneCode = useCallback(
    (number = phoneNumber, willLoad = true) => {
      setLoading(willLoad);
      createMfaMethod(mfaType.id, number, Date.now())
        .then((res) => {
          return sendSetupMfaCode(res.id);
        })
        .then(() => {
          setPhoneNumber(number); // saving number for resending
          routeToMfaVerification();
        })
        .catch((err) => {
          if (err.status === 401) {
            invalidateSession();
          } else {
            handleError(err);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [phoneNumber, mfaType, invalidateSession, routeToMfaVerification]
  );

  const setupNewMfa = (newMfaTypes) => {
    const formattedMfaTypes = newMfaTypes.map((type) => ({ type, id: type }));
    setOnSetupMfa(true);
    setMfaTypes(formattedMfaTypes);
    setMfaType(formattedMfaTypes?.[0]);
    navigate("mfa-selection");
  };

  const postLoginRedirect = (multitenant, user) => {
    if (multitenant) {
      return props.history.push({ pathname: "/select-user", search: location.search });
    }
    if (redirectUrl && redirectUrl !== window.location.origin) {
      if (mobile && window.history.state.authCodeFlow) {
        setMobileVerified(true);
      } else {
        return (window.location = redirectUrl);
      }
    } else {
      if (!redirectToOnboarding()) {
        const baseRoute = user?.role === "Client" ? "/m/" : "/";
        window.location = baseRoute;
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resend = useCallback(
    throttle(() => {
      // Resending shouldn't trigger loading state
      if (mfaType.type === "sms") {
        sendPhoneCode(phoneNumber, false);
      } else {
        sendVerificationCode(false);
      }
    }, 5000),
    [mfaType, sendPhoneCode, sendVerificationCode]
  );

  const renderTimeout = () => {
    return (
      <>
        <div
          {...scope}
          className={a("cp-title cp-mb-24 cp-wt-semibold")
            .m("cp-text-left", !whiteLabelSettings)
            .m("cp-mt-24 mobile-header", mobile)}
        >
          {bannerText}
        </div>
        <MfaTimeout routeToSignIn={routeToSignIn} />
      </>
    );
  };

  if (step === "mfa-timeout") {
    if (mobile) {
      return (
        <MobileDesktopWrapper mobile={mobile} whiteLabelSettings={whiteLabelSettings}>
          {renderTimeout()}
        </MobileDesktopWrapper>
      );
    } else {
      return renderTimeout();
    }
  }

  if (loading) return <CpLoader size="lg" className={tw("my-32")} />;
  return (
    <MobileDesktopWrapper mobile={mobile} whiteLabelSettings={whiteLabelSettings}>
      <div
        {...scope}
        className={a("cp-title cp-mb-24 cp-wt-semibold")
          .m("cp-text-left", !whiteLabelSettings)
          .m("cp-mt-24 mobile-header", mobile)}
      >
        {bannerText}
      </div>
      <div {...scope} className={m("cp-text-left mobile-mfa-wrapper", mobile)}>
        <CpSwitch expression={step}>
          <CpCase value="mfa-selection">
            <MfaType
              mfaType={mfaType}
              setMfaType={setMfaType}
              mfaTypes={mfaTypes}
              routeToSignIn={routeToSignIn}
              onSetupMfa={onSetupMfa}
              mobile={mobile}
              navigate={navigate}
              sendVerificationCode={sendVerificationCode}
            />
          </CpCase>
          <CpCase value="setup-mfa-phone">
            <SetupMfaPhone sendPhoneCode={sendPhoneCode} navigate={navigate} mobile={mobile} />
          </CpCase>
          <CpCase value="mfa-verification">
            <SubmitMfa
              mfaType={mfaType}
              navigate={navigate}
              postLoginRedirect={postLoginRedirect}
              resend={resend}
              invalidateSession={invalidateSession}
              setupNewMfa={setupNewMfa}
              onSetupMfa={onSetupMfa}
              mobile={mobile}
              uuid={uuid}
            />
          </CpCase>
        </CpSwitch>
        {mobileVerified && <MobileMfaVerified redirectUrl={redirectUrl} />}
      </div>
      {isWhiteLabelDomain && (
        <div {...scope} className="help-text">
          Having troubles? Contact your practitioner.
        </div>
      )}
      <CheckAuthCookieAndRefresh {...props} />
    </MobileDesktopWrapper>
  );
}

const css = k`
  .mobile-header {
    color: var(--cp-color-app-primary)
  }

  .mobile-mfa-wrapper .mobile-button-group .cp-button {
    height: 4.8rem;
    width: 100%;
  }

  .help-text {
    font-size: 1.2rem;
    color: var(--cp-color-app-secondary-text);
    text-align: left;
    margin-top: 1.6rem;
  }
`;
